import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * TagBadge - Display a colored tag with optional remove button
 * 
 * @param {object} tag - Tag object with { tag_id, name, color }
 * @param {boolean} removable - Show remove button
 * @param {function} onRemove - Callback when remove is clicked
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {string} className - Additional classes
 */
const TagBadge = ({ 
  tag, 
  removable = false, 
  onRemove, 
  size = 'sm',
  className,
  onClick
}) => {
  if (!tag) return null;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1.5',
    lg: 'text-base px-3 py-1.5 gap-2'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  // Calculate text color based on background brightness
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#1f2937' : '#ffffff';
  };

  const bgColor = tag.color || '#6366f1';
  const textColor = getContrastColor(bgColor);

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-full whitespace-nowrap transition-all",
        sizeClasses[size],
        onClick && "cursor-pointer hover:opacity-80",
        className
      )}
      style={{ 
        backgroundColor: bgColor,
        color: textColor
      }}
      onClick={onClick}
    >
      {tag.name}
      {removable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.(tag);
          }}
          className="ml-0.5 hover:bg-black/10 rounded-full p-0.5 transition-colors"
        >
          <X className={iconSizes[size]} />
        </button>
      )}
    </span>
  );
};

export default TagBadge;
